<template>
    <div class="modal fade" id="package-edit-modal" tabindex="-1" aria-labelledby="package-edit-modal-label" aria-hidden="true">
        <div class="modal-dialog modal-lg">
            <div class="modal-content">
                <div class="modal-header border-bottom-0 pb-0">
                    <div class="text-center w-100">
                        <button type="button" class="btn btn-white float-end close-modal" data-bs-dismiss="modal" data-bs-toggle="tooltip" title="Close">
                            <img class="icon" src="/img/icons/avid-icons/close.svg" alt=""> 
                        </button>
                        <h3 class="mt-2">{{ package.id > 0 ? 'Edit' : 'Add' }} Package</h3>
                    </div>
                </div>
                <div class="modal-body">
                    <div class="row">
                        <div class="col mb-3">
                            <label class="form-label" for="package.description">Description</label>
                            <input type="text" class="form-control" id="package.description" v-model="package.description" :class="{ 'is-invalid': errors.package_description }"
                                maxlength="256" :disabled="!canEdit('direct_mail')" aria-label="Description">
                            <div class="invalid-feedback" v-show="errors.package_description">
                                {{ errors.package_description }}
                            </div>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col mb-3">
                            <label class="form-label" for="package.letter">Pkg Code</label>
                            <input type="text" class="form-control" id="package.letter" v-model="package.letter" :class="{ 'is-invalid': errors.package_letter }" maxlength="32" :disabled="!canEdit('direct_mail')" aria-label="Pkg Code">
                            <div class="invalid-feedback" v-show="errors.package_letter">
                                {{ errors.package_letter }}
                            </div>
                        </div>
                        <div class="col mb-3">
                            <label class="form-label" for="package.color">Color 
                                <img class="info-icon" src="/img/icons/avid-icons/info.svg" data-bs-toggle="tooltip" title="Color will be used for selecting your audience on the next screen. It does not align with the actual printed letters." alt=""></img>
                            </label>
                            <div class="dropdown package-color">
                                <button class="form-select dropdown-toggle" type="button" id="package.color" data-bs-toggle="dropdown" aria-haspopup="true" aria-expanded="false" :style="errors.package_color ? {} : { backgroundColor: package.color }" :class="{ 'white-text': package.color && !errors.package_color, 'is-invalid': errors.package_color }" :disabled="!canEdit('direct_mail')">
                                    {{ package.color ? 'Color selected' : 'Choose a color' }}
                                </button>
                                <div class="dropdown-menu dropdown-menu-primary" aria-labelledby="package.color">
                                    <button class="dropdown-item" type="button" v-for="color in colors" :key="color" :style="{ backgroundColor: color}" @click="selectColor(color)">&nbsp;</button>
                                </div>
                            </div>
                            <div class="invalid-feedback" v-show="errors.package_color">
                                {{ errors.package_color }}
                            </div>
                        </div>
                        <div class="col mb-3">
                            <label class="form-label" for="package.appeal_code">Appeal Code 
                                <img class="info-icon" src="/img/icons/avid-icons/info.svg" data-bs-toggle="tooltip" title="The package appeal code will be used to identify this specific package version in reports and exports." alt=""></img>
                            </label>
                            <input type="text" class="form-control" id="package.appeal_code" v-model="package.appeal_code"
                                maxlength="64" :disabled="!canEdit('direct_mail')" aria-label="Appeal Code">
                        </div>
                    </div>
                    <div class="row">
                        <div class="col mt-4">
                            <div><em style="display: inline-block;padding: 0 20px 2px 0; ">Optional Package Details:</em>
                                <hr style="margin-top: -12px; margin-left:190px"></div>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col mb-3">
                            <label class="form-label" for="package.carrier_postage">Carrier Postage</label>
                            <select class="form-select" id="package.carrier_postage" v-model="package.carrier_postage" :disabled="!canEdit('direct_mail')" aria-label="Carrier Postage">
                                <option>Choose...</option>
                                <option v-for="postage in carrier_postage" :key="postage" :value="postage">{{ formatPostage(postage) }}</option>
                            </select>
                        </div>
                        <div class="col mb-3">
                            <label class="form-label" for="package.carrier_size">Carrier Size</label>
                            <select class="form-select" id="package.carrier_size" v-model="package.carrier_size" :disabled="!canEdit('direct_mail')" aria-label="Carrier Size">
                                <option>Choose...</option>
                                <option v-for="size in carrier_size" :key="size" :value="size">{{ formatSize(size) }}</option>
                            </select>
                        </div>
                        <div class="col mb-3">
                            <label class="form-label" for="package.letter_version">Letter Version</label>
                            <input type="text" class="form-control" id="package.letter_version" v-model="package.letter_version" :disabled="!canEdit('direct_mail')" aria-label="Letter Version">
                        </div>
                    </div>
                    <div class="row">
                        <div class="col mb-3">
                            <label class="form-label" for="package.reply_postage">Reply Postage</label>
                            <select class="form-select" id="package.reply_postage" v-model="package.reply_postage" :disabled="!canEdit('direct_mail')" aria-label="Reply Postage">
                                <option>Choose...</option>
                                <option v-for="postage in reply_postage" :key="postage" :value="postage">{{ formatPostage(postage) }}</option>
                            </select>
                        </div>
                        <div class="col mb-3">
                            <label class="form-label" for="package.reply_card_version">Reply Card Version</label>
                            <input type="text" class="form-control" id="package.reply_card_version" v-model="package.reply_card_version" :disabled="!canEdit('direct_mail')" aria-label="Reply Card Version">
                        </div>
                        <div class="col mb-3">
                            <label class="form-label" for="package.cost">Est. Package Cost</label>
                            <div class="input-group">
                                <span class="input-group-text" id="package.cost.dollar">$</span>
                                <input type="text" class="form-control" id="package.cost" v-model="package.cost" aria-describedby="package.cost.dollar"  @keypress="isNumber($event)" @paste="stripNonNumbers($event)" :disabled="!canEdit('direct_mail')" aria-label="Cost">
                            </div>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-3 mb-3">
                            <div class="custom-control custom-switch custom-switch-lg">
                                <input type="checkbox" class="form-check-input me-2" id="package.double_sided" v-model="doubleSidedValue" :disabled="!canEdit('direct_mail')" aria-label="Double Sided Value">
                                <label class="form-check-label ms-2" for="package.double_sided" :class="{ 'text-muted': !canEdit('direct_mail') }">Double Sided</label>
                            </div>
                        </div>
                        <div class="col mb-3">
                            <div class="custom-control custom-switch custom-switch-lg">
                                <input type="checkbox" class="form-check-input me-2" id="package.experiment" v-model="experimentValue" :disabled="!canEdit('direct_mail')" aria-label="Experiment Value">
                                <label class="form-check-label ms-2" for="package.experiment" :class="{ 'text-muted': !canEdit('direct_mail') }">Split for A/B Test</label>
                            </div>
                        </div>
                    </div>

                    <div v-show="package.experiment">
                        <div class="row">
                            <div class="col mt-4">
                                <div><em style="display: inline-block;padding: 0 20px 2px 0;">Experiment Treatment Details (Optional):</em><hr style="margin-top: -12px; margin-left:300px"></div>
                            </div>
                        </div>
                        <div class="row">
                            <div class="col">
                                <p>Any values entered below will be used in place of the default values entered above for constituents selected for the treatment group.</p>
                            </div>
                        </div>
                        <div class="row">
                            <div class="col mb-3">
                                <label class="form-label" for="package.experiment_description">Experiment Description</label>
                                <input type="text" class="form-control" id="package.experiment_description" v-model="package.experiment_description" maxlength="256" placeholder="Describe your A/B Split Test..." :disabled="!canEdit('direct_mail')" aria-label="Experiment Description">
                            </div>
                            <div class="col-2 mb-3">
                                <label class="form-label" for="package.experiment_letter">Package Code</label>
                                <input type="text" class="form-control" id="package.experiment_letter" v-model="package.experiment_letter" maxlength="32" :disabled="!canEdit('direct_mail')" aria-label="Package Code">
                            </div>
                            <div class="col-2 mb-3">
                                <label class="form-label" for="package.experiment_appeal_code">Appeal Code 
                                    <img class="info-icon" src="/img/icons/avid-icons/info.svg" data-bs-toggle="tooltip" title="The package appeal code will be used to identify this specific package version in reports and exports." alt=""></img>
                                </label>
                                <input type="text" class="form-control" id="package.experiment_appeal_code" v-model="package.experiment_appeal_code" maxlength="64" :disabled="!canEdit('direct_mail')" aria-label="Experiment Appeal Code">
                            </div>
                        </div>
                        <div class="row">
                            <div class="col mb-3">
                                <label class="form-label" for="package.experiment_carrier_postage">Carrier Postage</label>
                                <select class="form-select" id="package.experiment_carrier_postage" v-model="package.experiment_carrier_postage" :disabled="!canEdit('direct_mail')" aria-label="Carrier Postage">
                                    <option>Choose...</option>
                                    <option v-for="postage in carrier_postage" :key="postage" :value="postage">{{ formatPostage(postage) }}</option>
                                </select>
                            </div>
                            <div class="col mb-3">
                                <label class="form-label" for="package.experiment_carrier_size">Carrier Size</label>
                                <select class="form-select" id="package.experiment_carrier_size" v-model="package.experiment_carrier_size" :disabled="!canEdit('direct_mail')" aria-label="Carrier Size">
                                    <option>Choose...</option>
                                    <option v-for="size in carrier_size" :key="size" :value="size">{{ formatSize(size) }}</option>
                                </select>
                            </div>
                            <div class="col mb-3">
                                <label class="form-label" for="package.experiment_letter_version">Letter Version</label>
                                <input type="text" class="form-control" id="package.experiment_letter_version" v-model="package.experiment_letter_version" :disabled="!canEdit('direct_mail')" aria-label="Letter Version">
                            </div>
                        </div>
                        <div class="row">
                            <div class="col mb-3">
                                <label class="form-label" for="package.experiment_reply_postage">Reply Postage</label>
                                <select class="form-select" id="package.experiment_reply_postage" v-model="package.experiment_reply_postage" :disabled="!canEdit('direct_mail')" aria-label="Reply Postage">
                                    <option>Choose...</option>
                                    <option v-for="postage in reply_postage" :key="postage" :value="postage">{{ formatPostage(postage) }}</option>
                                </select>
                            </div>
                            <div class="col mb-3">
                                <label class="form-label" for="package.experiment_reply_card_version">Reply Card Version</label>
                                <input type="text" class="form-control" id="package.experiment_reply_card_version" v-model="package.experiment_reply_card_version" :disabled="!canEdit('direct_mail')" aria-label="Reply Card Version">
                            </div>
                            <div class="col mb-3">
                                <label class="form-label" for="package.experiment_cost">Est. Package Cost</label>
                                <div class="input-group">
                                    <span class="input-group-text" id="package.experiment_cost">$</span>
                                    <input type="text" class="form-control" id="package.experiment_cost" v-model="package.experiment_cost" aria-describedby="package.experiment_cost" @keypress="isNumber($event)" @paste="stripNonNumbers($event)" :disabled="!canEdit('direct_mail')" aria-label="Experiment Cost">
                                </div>
                            </div>
                            <div class="col mb-3">
                                <label class="form-label">&nbsp;</label>
                                <div class="custom-control custom-switch custom-switch-lg" style="margin-top:14px;">
                                    <input type="checkbox" class="form-check-input me-2" id="package.experiment_double_sided" v-model="experimentDoubleSidedValue" :disabled="!canEdit('direct_mail')" aria-label="&nbsp;">
                                    <label class="form-check-label" for="package.experiment_double_sided" :class="{ 'text-muted': !canEdit('direct_mail') }">Double Sided</label>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="row mt-2" v-if="form.error">
                        <div class="col">
                            <div class="alert alert-danger">
                                There was an issue saving your package. Check the fields above to get more information.
                            </div>
                        </div>
                    </div>
                </div>
                <div class="modal-footer">
                    <button v-if="canEdit('direct_mail')" type="button" class="btn btn-primary" @click="savePackage()" :disabled="actions.saving">
                        <span v-show="actions.saving"><div class="spinner-border spinner-border-sm" role="status"> <span class="visually-hidden">Saving...</span></div></span>
                        <span v-show="!actions.saving"><img class="icon" src="/img/icons/avid-icons/save.svg" alt=""> Save Package</span>
                    </button>
                    <button type="button" class="btn btn-secondary ms-2" @click="closeModal()">
                        <img class="icon" src="/img/icons/avid-icons/close.svg" alt=""> Cancel
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { defineComponent } from 'vue';
import permissionsMixin from '../../mixins/permissions';
import Swal from 'sweetalert2';

export default defineComponent({
    name: 'PackageModal',
    props: {
        show: {
            type: Boolean,
            default: false
        },
        package: {
            type: Object,
            required: true
        },
        direct_mail_id: {
            type: Number,
            required: true
        }
    },
    mixins: [permissionsMixin],
    data() {
        return {
            actions: {
                saving: false
            },
            form: {
                error: false
            },
            errors: {
                package_letter: '',
                package_description: '',
                package_color: ''
            },
            modal: null,
            colors: [
                // Greens (success)
                "#25473E", // $success-600
                "#46705F", // $success-500
                "#33A96E", // $success-400
                "#19CF78", // $success-300
                "#6BF3B2", // $success-200
                "#A7F8D1", // $success-100
                
                // Reds (error)
                "#D11F1F", // $error-600
                "#E23434", // $error-500
                "#FD4E4E", // $error-400
                "#FF7171", // $error-300
                "#FF9696", // $error-200
                "#FFB0B0", // $error-100
                
                // Yellows (warning)
                "#EBB500", // $warning-600
                "#F8BE00", // $warning-500
                "#FFCB20", // $warning-400
                "#FFDB66", // $warning-300
                "#FFE591", // $warning-200
                "#FFECAE"  // $warning-100
            ],

            carrier_postage: [
                'first_class', 'first_class_indicia', 'first_class_presort',
                'first_class_old_stamp', 'non_profit', 'non_profit_indicia',
                'non_profit_meter', 'non_profit_big_stamp', 'non_profit_two_stamps',
                'presorted_first_class', 'unstamped_rae', 'bre', 'rae',
                'rae_first_class', 'none'
            ],
            reply_postage: [
                'first_class', 'first_class_indicia', 'first_class_presort',
                'first_class_old_stamp', 'non_profit', 'non_profit_indicia',
                'non_profit_meter', 'non_profit_big_stamp', 'non_profit_two_stamps',
                'presorted_first_class', 'unstamped_rae', 'bre', 'rae',
                'rae_first_class', 'none'
            ],
            carrier_size: [
                '10', '11', '12', '14', '10_13', '11_6', '6_10', '6_11',
                '6_4', '6_9', '6_9.5', '9_12', '9_12_brownkraft',
                'executive', 'self'
            ]
        }
    },
    computed: {
        experimentValue: {
            get() {
                return Boolean(this.package.experiment);
            },
            set(value) {
                this.package.experiment = value;
            }
        },
        doubleSidedValue: {
            get() {
                return Boolean(this.package.double_sided);
            },
            set(value) {
                this.package.double_sided = value;
            }
        },
        experimentDoubleSidedValue: {
            get() {
                return Boolean(this.package.experiment_double_sided);
            },
            set(value) {
                this.package.experiment_double_sided = value;
            }
        }
    },
    mounted() {
        this.modal = new window.bootstrap.Modal(document.getElementById('package-edit-modal'));
        
        // Listen for Bootstrap modal hidden event to sync with Vue state
        const modalElement = document.getElementById('package-edit-modal');
        modalElement.addEventListener('hidden.bs.modal', () => {
            this.$emit('close');
        });
        
        this.$watch('show', (newVal) => {
            if (newVal) {
                // Reset errors when modal is opened
                this.errors = {
                    package_letter: '',
                    package_description: '',
                    package_color: ''
                };
                this.form.error = false;
                this.modal.show();
            } else {
                this.modal.hide();
            }
        });
    },
    methods: {
        formatPostage(str) {
            return (str === 'first_class') ? 'First-Class' :
                    (str === 'first_class_indicia') ? 'First-Class Indicia' :
                    (str === 'first_class_presort') ? 'First-Class Presort' :
                    (str === 'first_class_old_stamp') ? 'First-Class, Old Stamps' :
                    (str === 'non_profit') ? 'Non-Profit' :
                    (str === 'non_profit_indicia') ? 'Non-Profit Indicia' :
                    (str === 'non_profit_meter') ? 'Non-Profit Meter' :
                    (str === 'non_profit_big_stamp') ? 'Non-Profit, Big Stamp' :
                    (str === 'non_profit_two_stamps') ? 'Non-Profit, Two Stamps' :
                    (str === 'presorted_first_class') ? 'Presorted First-Class' :
                    (str === 'unstamped_rae') ? 'Unstamped RAE' :
                    (str === 'bre') ? 'BRE' :
                    (str === 'rae') ? 'RAE' :
                    (str === 'rae_first_class') ? 'RAE, First-Class' :
                    (str === 'none') ? 'None' : str;
        },
        formatSize(size) {
            return (size === '10') ? '#10' :
                    (size === '11') ? '#11' :
                    (size === '12') ? '#12' :
                    (size === '14') ? '#14' :
                    (size === '10_13') ? '10 x 13' :
                    (size === '11_6') ? '11 x 6' :
                    (size === '6_10') ? '6 x 10' :
                    (size === '6_11') ? '6 x 11' :
                    (size === '6_4') ? '6 x 4' :
                    (size === '6_9') ? '6 x 9' :
                    (size === '6_9.5') ? '6 x 9.5' :
                    (size === '9_12') ? '9 x 12' :
                    (size === '9_12_brownkraft') ? '9 x 12 Brownkraft' :
                    (size === 'executive') ? 'Executive' :
                    (size === 'self') ? 'Self' : size;
        },
        selectColor(color) {
            this.package.color = color;
            // Close the dropdown
            const dropdownEl = document.getElementById('package.color');
            const dropdown = bootstrap.Dropdown.getInstance(dropdownEl);
            if (dropdown) {
                dropdown.hide();
            }
        },
        validatePackage() {
            let isValid = true;
            this.errors.package_letter = '';
            this.errors.package_description = '';
            this.errors.package_color = '';

            if (!this.package.letter) {
                this.errors.package_letter = 'Package code is required.';
                isValid = false;
            }

            if (!this.package.description) {
                this.errors.package_description = 'Please add a package description.';
                isValid = false;
            }

            if (!this.package.color) {
                this.errors.package_color = 'Please choose a color.';
                isValid = false;
            }

            return isValid;
        },
        savePackage() {
            // Reset errors first
            this.errors = {
                package_letter: '',
                package_description: '',
                package_color: ''
            };
            this.form.error = false;

            if (!this.validatePackage()) {
                this.form.error = true;
                return;
            }

            this.actions.saving = true;
            this.package.direct_mail_id = this.direct_mail_id;

            if (this.package.id === 0) { // Create a new package
                axios.post('/api/direct_mail/package', this.package).then(response => {
                    this.actions.saving = false;
                    this.$emit('package-saved', response.data.package);
                    this.closeModal();
                }).catch(error => {
                    this.actions.saving = false;
                    this.handleApiError(error);
                });
            } else { // Update the package
                axios.put(`/api/direct_mail/package/${this.package.id}`, this.package).then(response => {
                    this.actions.saving = false;
                    this.$emit('package-updated', response.data.package);
                    this.closeModal();
                }).catch(error => {
                    this.actions.saving = false;
                    this.handleApiError(error);
                });
            }
        },
        handleApiError(error) {
            this.form.error = true;
            
            let errorMessage = 'An error occurred while saving the package. Please try again.';
            
            if (error.response) {
                // Handle validation errors
                if (error.response.status === 422 && error.response.data.errors) {
                    const validationErrors = error.response.data.errors;
                    
                    // Map backend validation errors to frontend error fields
                    if (validationErrors.letter) {
                        this.errors.package_letter = Array.isArray(validationErrors.letter) 
                            ? validationErrors.letter[0] 
                            : validationErrors.letter;
                    }
                    if (validationErrors.description) {
                        this.errors.package_description = Array.isArray(validationErrors.description) 
                            ? validationErrors.description[0] 
                            : validationErrors.description;
                    }
                    if (validationErrors.color) {
                        this.errors.package_color = Array.isArray(validationErrors.color) 
                            ? validationErrors.color[0] 
                            : validationErrors.color;
                    }
                    
                    // Show SweetAlert with validation error message
                    Swal.fire({
                        title: "Validation Error!",
                        text: error.response.data.message || "Please check the fields above and correct any errors.",
                        icon: "error",
                        customClass: {
                            popup: 'swal-error'
                        },
                    });
                    return;
                }
                
                // Handle other API errors
                if (error.response.data && error.response.data.message) {
                    errorMessage = error.response.data.message;
                } else if (error.response.status === 403) {
                    errorMessage = 'You do not have permission to perform this action.';
                } else if (error.response.status === 404) {
                    errorMessage = 'The requested resource was not found.';
                } else if (error.response.status === 500) {
                    errorMessage = 'A server error occurred. Please try again later.';
                }
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection and try again.';
            }
            
            Swal.fire({
                title: "Error!",
                text: errorMessage,
                icon: "error",
                customClass: {
                    popup: 'swal-error'
                },
            });
        },
        closeModal() {
            // Only prevent closing if currently saving
            if (this.actions.saving) {
                return;
            }
            
            // Reset errors and close modal
            this.$emit('close');
            this.errors = {
                package_letter: '',
                package_description: '',
                package_color: ''
            };
            this.form.error = false;
        },
        isNumber(evt) {
            evt = (evt) ? evt : window.event;
            var charCode = (evt.which) ? evt.which : evt.keyCode;
            if ((charCode > 31 && (charCode < 48 || charCode > 57)) && charCode !== 46 && charCode !== 44) {
                evt.preventDefault();
            } else {
                return true;
            }
        },
        stripNonNumbers(e) {
            e.preventDefault();
            e.stopPropagation();
            var paste = (e.clipboardData || window.clipboardData).getData('text/plain');
            paste = paste.replace(/[^\d.-]/g, '');
            if (document.queryCommandSupported("insertText")) {
                document.execCommand("insertText", false, paste);
            } else {
                document.execCommand("paste", false, paste);
            }
        }
    }
});
</script>
